import { useEffect, useState } from 'react';
import { ArrowUp, Check, ListPlus, Plus, Trash2 } from 'lucide-react';
import type {
  ChatInteractionSubmission,
  ServiceDefinitionFormInteraction,
} from '@/services/api';
import { cn } from '@/lib/utils';
import {
  buildServiceDefinitionSubmission,
  createServiceDefinitionFormRows,
  type ServiceDefinitionFormRow,
} from './serviceDefinitionFormState';
import { CarrierDiscoveryProgress } from './CarrierDiscoveryProgress';

interface ServiceDefinitionFormProps {
  interaction: ServiceDefinitionFormInteraction;
  disabled?: boolean;
  showDiscoveryProgress?: boolean;
  onSubmit: (
    interaction: ServiceDefinitionFormInteraction,
    submission: ChatInteractionSubmission
  ) => Promise<boolean>;
}

function createEmptyRow(): ServiceDefinitionFormRow {
  return {
    key: crypto.randomUUID(),
    code: '',
    name: '',
    description: '',
    aliasesText: '',
    minAcdText: '',
    maxAcdText: '',
  };
}

export function ServiceDefinitionForm({
  interaction,
  disabled = false,
  showDiscoveryProgress = false,
  onSubmit,
}: ServiceDefinitionFormProps) {
  const [rows, setRows] = useState<ServiceDefinitionFormRow[]>(() => (
    createServiceDefinitionFormRows(interaction.request.definitions)
  ));
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (interaction.status === 'submitted') {
      setSubmitting(false);
      setError(null);
      return;
    }
    setRows(createServiceDefinitionFormRows(interaction.request.definitions));
  }, [interaction.id, interaction.request.definitions, interaction.status]);

  if (interaction.status === 'submitted' && interaction.response) {
    return (
      <div className="mt-3 w-[min(44rem,calc(100vw-7rem))] max-w-full overflow-hidden rounded-xl border border-emerald-200 bg-emerald-50/70 shadow-sm dark:border-emerald-900/70 dark:bg-emerald-950/25">
        <div className="flex items-start gap-3 px-4 py-3.5">
          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white dark:bg-emerald-500">
            <Check className="h-3.5 w-3.5" strokeWidth={3} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
              Service Definitions submitted
            </p>
            <div className="mt-2 space-y-1.5">
              {interaction.response.definitions.map((definition) => (
                <div key={definition.id} className="flex gap-2 text-sm text-surface-800 dark:text-surface-100">
                  <span className="font-mono font-semibold">{definition.code}</span>
                  <span aria-hidden="true">—</span>
                  <span>{definition.name}</span>
                </div>
              ))}
            </div>
            {interaction.submittedBy && (
              <p className="mt-2 text-xs text-surface-500 dark:text-surface-400">
                Submitted by {interaction.submittedBy.username}
              </p>
            )}
            {!interaction.resumeCompleted && (
              <p className="mt-1 text-xs text-primary-600 dark:text-primary-300">
                The agent response is continuing.
              </p>
            )}
          </div>
        </div>
        {showDiscoveryProgress && (
          <CarrierDiscoveryProgress disabled={disabled} />
        )}
      </div>
    );
  }

  const updateRow = (
    key: string,
    field: keyof Pick<ServiceDefinitionFormRow, 'code' | 'name' | 'description' | 'aliasesText' | 'minAcdText' | 'maxAcdText'>,
    value: string
  ) => {
    setRows((current) => current.map((row) => (
      row.key === key ? { ...row, [field]: value } : row
    )));
    setError(null);
  };

  const handleSubmit = async () => {
    if (disabled || submitting) return;
    const submission = buildServiceDefinitionSubmission(rows);
    if (!submission.ok) {
      setError(submission.error);
      return;
    }
    setSubmitting(true);
    const accepted = await onSubmit(interaction, {
      definitions: submission.definitions,
    });
    if (!accepted) setSubmitting(false);
  };

  return (
    <div className="mt-3 w-[min(44rem,calc(100vw-7rem))] max-w-full overflow-hidden rounded-xl border border-surface-200 bg-white shadow-sm dark:border-surface-700 dark:bg-surface-900">
      <div className="border-b border-surface-100 px-4 py-3.5 dark:border-surface-800">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-primary-600 dark:text-primary-300">
          <ListPlus className="h-4 w-4" />
          Service Definition Form
        </div>
        <h3 className="mt-2 text-sm font-semibold text-surface-900 dark:text-surface-100">
          {interaction.request.title}
        </h3>
        <p className="mt-1 text-xs leading-5 text-surface-600 dark:text-surface-300">
          {interaction.request.instructions}
        </p>
      </div>

      <div className="max-h-[32rem] space-y-3 overflow-y-auto p-3">
        {rows.map((row, index) => (
          <fieldset
            key={row.key}
            disabled={disabled || submitting}
            className="rounded-lg border border-surface-200 p-3 dark:border-surface-700"
          >
            <div className="mb-3 flex items-center justify-between">
              <legend className="text-xs font-semibold text-surface-700 dark:text-surface-200">
                Service {index + 1}
              </legend>
              <button
                type="button"
                aria-label={`Remove service ${index + 1}`}
                disabled={rows.length === 1 || disabled || submitting}
                onClick={() => setRows((current) => current.filter((candidate) => candidate.key !== row.key))}
                className="rounded-md p-1.5 text-surface-400 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-30 dark:hover:bg-red-950/30"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-xs font-medium text-surface-700 dark:text-surface-200">
                Exact service code
                <input
                  value={row.code}
                  maxLength={128}
                  onChange={(event) => updateRow(row.key, 'code', event.target.value)}
                  placeholder="PREM SD"
                  className="mt-1.5 h-9 w-full rounded-md border border-surface-300 bg-white px-3 font-mono text-sm text-surface-900 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 dark:border-surface-600 dark:bg-surface-950 dark:text-surface-100"
                />
              </label>
              <label className="text-xs font-medium text-surface-700 dark:text-surface-200">
                Service name
                <input
                  value={row.name}
                  maxLength={256}
                  onChange={(event) => updateRow(row.key, 'name', event.target.value)}
                  placeholder="Premium Short Duration"
                  className="mt-1.5 h-9 w-full rounded-md border border-surface-300 bg-white px-3 text-sm text-surface-900 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 dark:border-surface-600 dark:bg-surface-950 dark:text-surface-100"
                />
              </label>
            </div>
            <label className="mt-3 block text-xs font-medium text-surface-700 dark:text-surface-200">
              Description <span className="font-normal text-surface-400">(optional)</span>
              <textarea
                value={row.description}
                maxLength={1_000}
                rows={2}
                onChange={(event) => updateRow(row.key, 'description', event.target.value)}
                className="mt-1.5 w-full resize-y rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 dark:border-surface-600 dark:bg-surface-950 dark:text-surface-100"
              />
            </label>
            <label className="mt-3 block text-xs font-medium text-surface-700 dark:text-surface-200">
              Aliases <span className="font-normal text-surface-400">(optional, one per line)</span>
              <textarea
                value={row.aliasesText}
                rows={2}
                onChange={(event) => updateRow(row.key, 'aliasesText', event.target.value)}
                className="mt-1.5 w-full resize-y rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-900 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 dark:border-surface-600 dark:bg-surface-950 dark:text-surface-100"
              />
            </label>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label className="text-xs font-medium text-surface-700 dark:text-surface-200">
                ACD from (seconds) <span className="font-normal text-surface-400">(optional)</span>
                <input
                  value={row.minAcdText}
                  inputMode="numeric"
                  maxLength={4}
                  onChange={(event) => updateRow(row.key, 'minAcdText', event.target.value)}
                  placeholder="15"
                  className="mt-1.5 h-9 w-full rounded-md border border-surface-300 bg-white px-3 text-sm text-surface-900 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 dark:border-surface-600 dark:bg-surface-950 dark:text-surface-100"
                />
              </label>
              <label className="text-xs font-medium text-surface-700 dark:text-surface-200">
                ACD below (seconds) <span className="font-normal text-surface-400">(optional)</span>
                <input
                  value={row.maxAcdText}
                  inputMode="numeric"
                  maxLength={4}
                  onChange={(event) => updateRow(row.key, 'maxAcdText', event.target.value)}
                  placeholder="30"
                  className="mt-1.5 h-9 w-full rounded-md border border-surface-300 bg-white px-3 text-sm text-surface-900 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 dark:border-surface-600 dark:bg-surface-950 dark:text-surface-100"
                />
              </label>
            </div>
            <p className="mt-1.5 text-[0.68rem] text-surface-500 dark:text-surface-400">
              TelcoOS uses the range only to propose this Service for trunks whose name contains an ACD, such as 25ACD.
            </p>
          </fieldset>
        ))}
        <button
          type="button"
          disabled={disabled || submitting || rows.length >= 100}
          onClick={() => setRows((current) => [...current, createEmptyRow()])}
          className="flex items-center gap-2 rounded-md px-2 py-1.5 text-xs font-semibold text-primary-700 hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-primary-300 dark:hover:bg-primary-950/30"
        >
          <Plus className="h-4 w-4" />
          Add service
        </button>
        {error && (
          <p role="alert" className="text-xs font-medium text-red-600 dark:text-red-400">
            {error}
          </p>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-surface-100 px-3 py-2.5 dark:border-surface-800">
        <span className="px-1 text-xs text-surface-500 dark:text-surface-400">
          {rows.length} {rows.length === 1 ? 'service' : 'services'}
        </span>
        <button
          type="button"
          aria-label="Submit Service Definitions"
          disabled={disabled || submitting}
          onClick={() => void handleSubmit()}
          className={cn(
            'flex h-8 items-center gap-2 rounded-lg px-3 text-xs font-semibold transition-all',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/50',
            disabled || submitting
              ? 'cursor-not-allowed bg-surface-100 text-surface-400 dark:bg-surface-800 dark:text-surface-600'
              : 'bg-surface-900 text-white hover:bg-surface-700 active:scale-95 dark:bg-white dark:text-surface-950 dark:hover:bg-surface-200'
          )}
        >
          Submit
          <ArrowUp className={cn('h-4 w-4', submitting && 'animate-pulse')} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}
